"use client";

import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check, ChevronDown, Sparkles } from "lucide-react";
import { usePlans } from "@/hooks/use-plans";

export function ContactSection() {
  const { plans } = usePlans();
  const PLAN_OPTIONS = [
    ...Object.values(plans)
      .filter((p) => p.visible)
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((p) => `${p.name}${p.price ? ` – ${p.price}` : ""}`),
    "Just exploring",
  ];
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    plan: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [planDropdownOpen, setPlanDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setPlanDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const validateEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    if (!validateEmail(formData.email)) {
      setError("Please enter a valid email address.");
      setIsSubmitting(false);
      return;
    }

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim().toLowerCase(),
          company: formData.company.trim(),
          plan: formData.plan,
          message: formData.message.trim(),
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Something went wrong.");
      }

      setIsSuccess(true);
      setFormData({ name: "", email: "", company: "", plan: "", message: "" });
    } catch (err: any) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="relative" style={{ paddingTop: "clamp(5rem, 12vh, 10rem)", paddingBottom: "clamp(5rem, 12vh, 10rem)" }}>
      <div className="max-w-5xl mx-auto px-6 relative z-10">
        <AnimatePresence mode="wait">
          {!isSuccess ? (
            <motion.div
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {/* Header */}
              <div className="grid lg:grid-cols-[1fr_1.2fr] gap-8 lg:gap-16 mb-16 md:mb-20">
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground"
                >
                  Get in<br />
                  <span className="text-gradient">touch.</span>
                </motion.h2>

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 }}
                  className="text-foreground-secondary text-base md:text-lg lg:self-end lg:pb-2 max-w-md"
                >
                  Start your free trial or reach out with any questions. We&apos;ll get back to you within one business day.
                </motion.p>
              </div>

              {/* Form */}
              <motion.form
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                onSubmit={handleSubmit}
              >
                {/* Row 1: Name + Email */}
                <div className="grid md:grid-cols-2 gap-10 md:gap-12 mb-10 md:mb-12">
                  <div className="group">
                    <label className="block text-sm text-foreground-secondary mb-3 uppercase tracking-wider">
                      Full Name
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="w-full bg-transparent border-b border-foreground/20 py-3 text-foreground text-lg placeholder:text-foreground-muted/60 focus:outline-none focus:border-accent transition-colors duration-300"
                      placeholder="Your name"
                    />
                  </div>

                  <div className="group">
                    <label className="block text-sm text-foreground-secondary mb-3 uppercase tracking-wider">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="w-full bg-transparent border-b border-foreground/20 py-3 text-foreground text-lg placeholder:text-foreground-muted/60 focus:outline-none focus:border-accent transition-colors duration-300"
                      placeholder="you@company.com"
                    />
                  </div>
                </div>

                {/* Row 2: Company + Plan */}
                <div className="grid md:grid-cols-2 gap-10 md:gap-12 mb-10 md:mb-12">
                  <div className="group">
                    <label className="block text-sm text-foreground-secondary mb-3 uppercase tracking-wider">
                      Brand / Company <span className="normal-case text-foreground-muted">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="w-full bg-transparent border-b border-foreground/20 py-3 text-foreground text-lg placeholder:text-foreground-muted/60 focus:outline-none focus:border-accent transition-colors duration-300"
                      placeholder="Your brand or company"
                    />
                  </div>

                  <div className="group" ref={dropdownRef}>
                    <label className="block text-sm text-foreground-secondary mb-3 uppercase tracking-wider">
                      Interested In <span className="normal-case text-foreground-muted">(optional)</span>
                    </label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setPlanDropdownOpen(!planDropdownOpen)}
                        className="w-full bg-transparent border-b py-3 text-lg text-left focus:outline-none transition-colors duration-300 cursor-pointer flex items-center justify-between"
                        style={{
                          borderColor: planDropdownOpen ? "var(--accent)" : "color-mix(in srgb, var(--foreground) 20%, transparent)",
                          color: formData.plan ? "var(--foreground)" : "color-mix(in srgb, var(--foreground-muted) 60%, transparent)",
                        }}
                      >
                        <span>{formData.plan || "Select a plan"}</span>
                        <ChevronDown
                          className={`w-5 h-5 text-foreground-muted transition-transform duration-200 ${planDropdownOpen ? "rotate-180" : ""}`}
                        />
                      </button>
                      <AnimatePresence>
                        {planDropdownOpen && (
                          <motion.ul
                            initial={{ opacity: 0, y: -4 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -4 }}
                            transition={{ duration: 0.15 }}
                            className="absolute z-50 left-0 right-0 mt-1 rounded-lg border border-foreground/10 bg-background/95 backdrop-blur-xl shadow-xl overflow-hidden"
                          >
                            {PLAN_OPTIONS.map((p) => (
                              <li
                                key={p}
                                onClick={() => {
                                  setFormData({ ...formData, plan: p });
                                  setPlanDropdownOpen(false);
                                }}
                                className="px-4 py-3 text-foreground/80 hover:text-foreground hover:bg-foreground/5 cursor-pointer transition-colors duration-150 text-[15px]"
                              >
                                {p}
                              </li>
                            ))}
                          </motion.ul>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                </div>

                {/* Row 3: Message */}
                <div className="mb-12 md:mb-16">
                  <label className="block text-sm text-foreground-secondary mb-3 uppercase tracking-wider">
                    Message <span className="normal-case text-foreground-muted">(optional)</span>
                  </label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={3}
                    className="w-full bg-transparent border-b border-foreground/20 py-3 text-foreground text-lg placeholder:text-foreground-muted/60 focus:outline-none focus:border-accent transition-colors duration-300 resize-none"
                    placeholder="Tell us about your brand, goals, or any questions..."
                  />
                </div>

                {/* Submit */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-6">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-8 py-4 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center gap-2 group disabled:opacity-70 disabled:cursor-not-allowed hover:shadow-lg hover:shadow-accent/20 w-full sm:w-auto"
                    style={{ background: "linear-gradient(135deg, #10b981 0%, #059669 100%)", color: "white" }}
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <span>Send Message</span>
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </button>

                  {error && (
                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="text-red-400 text-sm"
                    >
                      {error}
                    </motion.p>
                  )}
                </div>
              </motion.form>
            </motion.div>
          ) : (
            <motion.div
              key="success"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="py-12"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                className="w-16 h-16 rounded-full mb-8 flex items-center justify-center"
                style={{
                  background: "linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(163, 230, 53, 0.1) 100%)",
                  border: "1px solid rgba(16, 185, 129, 0.3)",
                }}
              >
                <Check className="w-8 h-8 text-accent" />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <h3 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                  Message received.
                </h3>
                <p className="text-foreground-secondary text-lg mb-8 max-w-md">
                  We&apos;ll be in touch within one business day.<br />
                  Check your inbox for a confirmation.
                </p>

                <div className="flex items-center gap-2 text-accent mb-10">
                  <Sparkles className="w-5 h-5" />
                  <span className="font-medium">Let&apos;s build something great together</span>
                </div>

                <button
                  onClick={() => setIsSuccess(false)}
                  className="text-foreground-muted hover:text-foreground text-sm transition-colors underline underline-offset-4"
                >
                  Send another message
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
